import { Injectable } from '@angular/core';
import { ApiService, StorageLevelToCustomerLevelNamePipe } from '@twaice-fe/frontend/shared/services';
import { StorageLevelV2 } from '@twaice-fe/shared/models';
import { map, Observable } from 'rxjs';
import {
  BreachedRule,
  RuleEvaluation,
  SmartAlertsBreachesParams,
  SmartAlertsRule,
  SmartAlertsRuleEvaluationResponse,
  SmartAlertsRuleEvaluationsParams,
  SmartAlertsRuleVersion,
  SmartAlertsSystemParams,
} from './component-sidebar.types';

@Injectable()
export class SmartAlertsApiService {
  constructor(
    private apiService: ApiService,
    private customerLevelTranslationPipe: StorageLevelToCustomerLevelNamePipe
  ) {}

  fetchBreachedRules({
    customerBk,
    systemBk,
    ruleTypes,
    date,
    componentTypes,
  }: SmartAlertsBreachesParams): Observable<{ breachedRules: BreachedRule[] }> {
    return this.apiService.fetchData<
      { date: string; ruleTypes: string; componentTypes: string | undefined },
      { breachedRules: BreachedRule[] }
    >(`${this.getBaseUrl({ customerBk, systemBk })}/breaches`, {
      date: new Date(date).toISOString().split('T')[0],
      ruleTypes: ruleTypes.join(','),
      componentTypes: this.sanitizeComponentTypes(componentTypes),
    });
  }

  fetchRules<TRule extends SmartAlertsRule>({ customerBk, systemBk }: SmartAlertsSystemParams): Observable<TRule[]> {
    return this.apiService.fetchData<null, { rules: TRule[] }>(`${this.getBaseUrl({ customerBk, systemBk })}/rules`).pipe(
      map(({ rules }) =>
        rules.map((rule) => {
          const latestVersion = this.getLatestVersion(rule.versions as SmartAlertsRuleVersion[]);
          return {
            ...rule,
            currentVersion: rule.versions[rule.versions.length - 1],
            relationalSensors: latestVersion?.relationalSensors ?? [],
            minDurationSeconds: latestVersion?.minDurationSeconds ?? null,
            ruleName: this.getRuleName(rule, { customerBk, systemBk }),
          };
        })
      )
    );
  }

  fetchComponentRuleEvaluations({
    customerBk,
    systemBk,
    ruleBk,
    componentBk,
    start,
    end,
  }: SmartAlertsRuleEvaluationsParams): Observable<RuleEvaluation[]> {
    return this.apiService
      .fetchData<{ start: string; end: string }, SmartAlertsRuleEvaluationResponse[]>(
        `${this.getBaseUrl({ customerBk, systemBk })}/rules/${ruleBk}/components/${componentBk}`,
        {
          start: (start as Date).toISOString(),
          end: (end as Date).toISOString(),
        }
      )
      .pipe(
        map((ruleEvaluations) =>
          ruleEvaluations.map((ruleEvaluation) => ({
            ...ruleEvaluation,
            start: new Date(ruleEvaluation.start),
            end: new Date(ruleEvaluation.end),
          }))
        )
      );
  }

  // Same pick as the configuration page, so both show the same conditions even if versions arrive unsorted.
  private getLatestVersion(versions: SmartAlertsRuleVersion[]): SmartAlertsRuleVersion | undefined {
    return versions.reduce<SmartAlertsRuleVersion | undefined>(
      (newest, version) => (!newest || version.versionNumber > newest.versionNumber ? version : newest),
      undefined
    );
  }

  // System disconnected rules come without a real name; a user-named rule on that sensor keeps its own.
  private getRuleName({ kpi, ruleName }: SmartAlertsRule, system: SmartAlertsSystemParams): string {
    const hasOwnName = !!ruleName && ruleName.toLowerCase() !== kpi.toLowerCase();
    if (kpi !== 'disconnected' || hasOwnName) return ruleName;

    return 'Disconnected ' + this.customerLevelTranslationPipe.transform(StorageLevelV2.String, true, 1, false, system);
  }

  private getBaseUrl(params: SmartAlertsSystemParams): string {
    return `analytics/alerts/customers/${params.customerBk}/systems/${params.systemBk}/v2`;
  }

  private sanitizeComponentTypes(componentTypes: string[] | null = null): string | undefined {
    return componentTypes?.join(',') || undefined;
  }
}
